import React from "react";
import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { Search, Image, Video, Loader2, Check, X, Link2, ImagePlus } from "lucide-react";
import {
  useCreativeLibrary,
  filterCreativesByAspect,
  filterCreativesByType,
  type Creative,
} from "@/hooks/useCreativeLibrary";

interface CreativeLibraryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (feed: Creative, vertical: Creative | null, horizontal: Creative | null) => void;
  /** Called instead of onSelect when singleSelectMode is true — returns just one creative */
  onSelectSingle?: (creative: Creative) => void;
  adAccountId?: string;
  /** Filter to show only specific aspect ratio */
  aspectFilter?: "feed" | "vertical" | "horizontal" | "all";
  /** Ad format of the current row */
  adFormat?: string;
  /** When true, shows individual (unpaired) assets and allows selecting exactly one */
  singleSelectMode?: boolean;
}

type AspectTab = "all" | "feed" | "vertical" | "horizontal";
type TypeFilter = "all" | "image" | "video";
type SelectionMode = "feed" | "vertical" | "horizontal";

export function CreativeLibraryModal({
  open,
  onOpenChange,
  onSelect,
  onSelectSingle,
  adAccountId,
  aspectFilter,
  adFormat,
  singleSelectMode = false,
}: CreativeLibraryModalProps) {
  const { creatives, isLoading, error } = useCreativeLibrary(adAccountId);

  const [searchQuery, setSearchQuery] = useState("");
  const [aspectTab, setAspectTab] = useState<AspectTab>(aspectFilter || "feed");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [selectedFeedId, setSelectedFeedId] = useState<string | null>(null);
  const [selectedVerticalId, setSelectedVerticalId] = useState<string | null>(null);
  const [selectedHorizontalId, setSelectedHorizontalId] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>("feed");

  // Build a map of groupId -> creatives for pairing lookups
  const groupMap = useMemo(() => {
    const map = new Map<string, Creative[]>();
    for (const c of creatives) {
      if (c.groupId) {
        const list = map.get(c.groupId) || [];
        list.push(c);
        map.set(c.groupId, list);
      }
    }
    return map;
  }, [creatives]);

  // Find the paired vertical for a given creative
  const findPairedVertical = useCallback(
    (creative: Creative): Creative | null => {
      if (!creative.groupId) return null;
      const group = groupMap.get(creative.groupId);
      if (!group) return null;
      return group.find(
        (c) => c.aspectRatioCategory === "vertical" && c.id !== creative.id
      ) || null;
    },
    [groupMap]
  );

  // Check if a creative has any pair in its group
  const hasPair = useCallback(
    (creative: Creative): boolean => {
      if (!creative.groupId) return false;
      const group = groupMap.get(creative.groupId);
      if (!group) return false;
      return group.some((c) => c.id !== creative.id);
    },
    [groupMap]
  );

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedFeedId(null);
      setSelectedVerticalId(null);
      setSelectedHorizontalId(null);
      setSelectionMode("feed");
      setSearchQuery("");
      setAspectTab(aspectFilter || "feed");
      setTypeFilter("all");
    }
  }, [open, aspectFilter]);

  // Resolve selected creatives
  const selectedFeed = useMemo(
    () => creatives.find((c) => c.id === selectedFeedId) || null,
    [creatives, selectedFeedId]
  );
  const selectedVertical = useMemo(
    () => creatives.find((c) => c.id === selectedVerticalId) || null,
    [creatives, selectedVerticalId]
  );
  const selectedHorizontal = useMemo(
    () => creatives.find((c) => c.id === selectedHorizontalId) || null,
    [creatives, selectedHorizontalId]
  );

  // Filter creatives based on search, aspect ratio, type, and selection mode
  const filteredCreatives = useMemo(() => {
    let result = creatives;

    if (singleSelectMode) {
      // In single-select mode (Collection cover), only show feed-aspect assets
      result = filterCreativesByAspect(result, "feed");
    } else if (selectionMode === "vertical") {
      result = filterCreativesByAspect(result, "vertical");
    } else if (selectionMode === "horizontal") {
      result = filterCreativesByAspect(result, "horizontal");
    } else {
      result = filterCreativesByAspect(result, aspectTab);
    }

    // Apply type filter
    result = filterCreativesByType(result, typeFilter);

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((c) =>
        c.filename.toLowerCase().includes(query)
      );
    }

    return result;
  }, [creatives, aspectTab, typeFilter, searchQuery, selectionMode]);

  const handleConfirm = () => {
    if (selectedFeed) {
      onSelect(selectedFeed, selectedVertical, selectedHorizontal);
      onOpenChange(false);
    }
  };

  const handleCreativeClick = (creative: Creative) => {
    // Single-select mode: click = select + confirm immediately
    if (singleSelectMode) {
      if (onSelectSingle) {
        onSelectSingle(creative);
      }
      onOpenChange(false);
      return;
    }

    if (selectionMode === "vertical") {
      setSelectedVerticalId(creative.id);
      setSelectionMode("feed");
      return;
    }

    if (selectionMode === "horizontal") {
      setSelectedHorizontalId(creative.id);
      setSelectionMode("feed");
      return;
    }

    // Feed mode: double-click behavior for quick select
    if (selectedFeedId === creative.id) {
      const feed = creatives.find((c) => c.id === selectedFeedId);
      if (feed) {
        const vertical = creatives.find((c) => c.id === selectedVerticalId) || null;
        const horizontal = creatives.find((c) => c.id === selectedHorizontalId) || null;
        onSelect(feed, vertical, horizontal);
        onOpenChange(false);
      }
      return;
    }

    // Single click: select as feed
    setSelectedFeedId(creative.id);

    // Auto-pair: find vertical in same group
    const pairedVertical = findPairedVertical(creative);
    if (pairedVertical) {
      setSelectedVerticalId(pairedVertical.id);
    } else {
      setSelectedVerticalId(null);
    }
  };

  const handleEnterVerticalMode = () => {
    setSelectionMode("vertical");
    setSearchQuery("");
  };

  const handleCancelVerticalMode = () => {
    setSelectionMode("feed");
  };

  const handleEnterHorizontalMode = () => {
    setSelectionMode("horizontal");
    setSearchQuery("");
  };

  const handleCancelHorizontalMode = () => {
    setSelectionMode("feed");
  };

  const handleClearFeed = () => {
    setSelectedFeedId(null);
    setSelectedVerticalId(null);
    setSelectedHorizontalId(null);
  };

  const handleClearVertical = () => {
    setSelectedVerticalId(null);
  };

  const handleClearHorizontal = () => {
    setSelectedHorizontalId(null);
  };

  const hasSelection = !!selectedFeedId;
  const isSingleFormat = adFormat === "Single Image or Video";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {singleSelectMode
              ? "Select Cover Creative"
              : selectionMode === "vertical"
              ? "Select Vertical Creative"
              : selectionMode === "horizontal"
              ? "Select Horizontal Creative"
              : "Select Creative"}
          </DialogTitle>
        </DialogHeader>

        {/* Vertical mode banner */}
        {!singleSelectMode && selectionMode === "vertical" && (
          <div className="flex items-center justify-between bg-primary/10 border border-primary/20 rounded-md px-3 py-2">
            <p className="text-sm text-primary">
              Select a vertical (9:16) creative for Stories, Reels & Search placements
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={handleCancelVerticalMode}
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Horizontal mode banner */}
        {!singleSelectMode && selectionMode === "horizontal" && (
          <div className="flex items-center justify-between bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Select a horizontal (16:9) creative for in-stream & search placements
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={handleCancelHorizontalMode}
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-4 py-2">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by filename..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9"
            />
          </div>

          {/* Aspect Ratio Tabs - hidden in vertical/horizontal mode, when aspectFilter is set, and in singleSelectMode */}
          {!aspectFilter && !singleSelectMode && selectionMode === "feed" && (
            <Tabs value={aspectTab} onValueChange={(v) => setAspectTab(v as AspectTab)}>
              <TabsList className="h-9">
                <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
                <TabsTrigger value="feed" className="text-xs">Feed</TabsTrigger>
                <TabsTrigger value="vertical" className="text-xs">Vertical</TabsTrigger>
                <TabsTrigger value="horizontal" className="text-xs">Horizontal</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {/* Type Filter */}
          <div className="flex items-center gap-1">
            <Button
              variant={typeFilter === "all" ? "secondary" : "ghost"}
              size="sm"
              className="h-9"
              onClick={() => setTypeFilter("all")}
            >
              All
            </Button>
            <Button
              variant={typeFilter === "image" ? "secondary" : "ghost"}
              size="sm"
              className="h-9 gap-1"
              onClick={() => setTypeFilter("image")}
            >
              <Image className="h-3.5 w-3.5" />
              Images
            </Button>
            <Button
              variant={typeFilter === "video" ? "secondary" : "ghost"}
              size="sm"
              className="h-9 gap-1"
              onClick={() => setTypeFilter("video")}
            >
              <Video className="h-3.5 w-3.5" />
              Videos
            </Button>
          </div>
        </div>

        {/* Creative Grid */}
        <div className="flex-1 overflow-auto min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-48 text-destructive">
              Failed to load creatives. Please try again.
            </div>
          ) : filteredCreatives.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-muted-foreground">
              {creatives.length === 0
                ? "No creatives uploaded yet."
                : "No creatives match your filters."}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-3 p-1">
              {filteredCreatives.map((creative) => {
                const isFeedSelected = selectedFeedId === creative.id;
                const isVerticalSelected = selectedVerticalId === creative.id;
                const isHorizontalSelected = selectedHorizontalId === creative.id;
                const isSelected = isFeedSelected || isVerticalSelected || isHorizontalSelected;
                const isPaired = hasPair(creative);

                return (
                  <button
                    key={creative.id}
                    onClick={() => handleCreativeClick(creative)}
                    className={cn(
                      "relative aspect-square rounded-lg overflow-hidden border-2 transition-all",
                      "hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/50",
                      isSelected
                        ? "border-primary ring-2 ring-primary/30"
                        : "border-border"
                    )}
                  >
                    {/* Thumbnail */}
                    {creative.thumbnailUrl ? (
                      <img
                        src={creative.thumbnailUrl}
                        alt={creative.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        {creative.type === "video" ? (
                          <Video className="h-8 w-8 text-muted-foreground" />
                        ) : (
                          <Image className="h-8 w-8 text-muted-foreground" />
                        )}
                      </div>
                    )}

                    {/* Type badge */}
                    {creative.type === "video" && (
                      <div className="absolute top-1.5 right-1.5 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
                        VIDEO
                      </div>
                    )}

                    {/* Aspect ratio badge */}
                    {creative.aspectRatioCategory && (
                      <div className="absolute bottom-1.5 left-1.5 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded capitalize">
                        {creative.aspectRatioCategory}
                      </div>
                    )}

                    {/* Paired badge - hidden in single select mode */}
                    {!singleSelectMode && isPaired && selectionMode === "feed" && (
                      <div className="absolute bottom-1.5 right-1.5 bg-emerald-600/90 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5">
                        <Link2 className="h-2.5 w-2.5" />
                        Paired
                      </div>
                    )}

                    {/* Selection checkmark with role label */}
                    {isFeedSelected && (
                      <div className="absolute top-1.5 left-1.5 bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 flex items-center gap-1 text-[10px] font-medium">
                        <Check className="h-3 w-3" />
                        Feed
                      </div>
                    )}
                    {isVerticalSelected && (
                      <div className="absolute top-1.5 left-1.5 bg-violet-600 text-white rounded-full px-1.5 py-0.5 flex items-center gap-1 text-[10px] font-medium">
                        <Check className="h-3 w-3" />
                        Vertical
                      </div>
                    )}
                    {isHorizontalSelected && (
                      <div className="absolute top-1.5 left-1.5 bg-amber-600 text-white rounded-full px-1.5 py-0.5 flex items-center gap-1 text-[10px] font-medium">
                        <Check className="h-3 w-3" />
                        Horizontal
                      </div>
                    )}

                    {/* Filename on hover */}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 hover:opacity-100 transition-opacity">
                      <p className="text-white text-xs truncate">{creative.filename}</p>
                      {creative.dimensions && (
                        <p className="text-white/70 text-[10px]">{creative.dimensions}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Selection Preview - hidden in single select mode */}
        {!singleSelectMode && hasSelection && (
          <div className="border-t pt-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">Selected Creatives</p>
            <div className="flex items-start gap-3">
              {/* Feed card */}
              <div className="flex flex-col items-center gap-1.5">
                <div className="relative h-32 w-40 rounded-lg border border-primary bg-muted/30 flex items-center justify-center">
                  {selectedFeed?.thumbnailUrl ? (
                    <img
                      src={selectedFeed.thumbnailUrl}
                      alt="Feed"
                      className="h-28 w-28 rounded-md object-cover"
                    />
                  ) : (
                    <div className="h-28 w-28 rounded-md bg-muted flex items-center justify-center">
                      <Image className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <button
                    onClick={handleClearFeed}
                    className="absolute top-1.5 right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 hover:bg-destructive/80"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
                <span className="text-[10px] font-medium text-primary uppercase tracking-wide">Feed</span>
                <span className="text-xs text-muted-foreground truncate max-w-[140px]">
                  {selectedFeed?.filename || "—"}
                </span>
                {selectedFeed?.groupId && selectedVertical && (
                  <span className="text-[10px] text-emerald-600 flex items-center gap-0.5">
                    <Link2 className="h-2.5 w-2.5" />
                    Auto-paired
                  </span>
                )}
              </div>

              {/* Plus separator */}
              <div className="flex items-center h-32 text-muted-foreground text-lg font-light">+</div>

              {/* Vertical card */}
              <div className="flex flex-col items-center gap-1.5">
                {selectedVertical ? (
                  <>
                    <div className="relative h-32 w-40 rounded-lg border border-violet-500 bg-muted/30 flex items-center justify-center">
                      <img
                        src={selectedVertical.thumbnailUrl || ""}
                        alt="Vertical"
                        className="h-28 w-[63px] rounded-md object-cover"
                      />
                      <button
                        onClick={handleClearVertical}
                        className="absolute top-1.5 right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 hover:bg-destructive/80"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                    <span className="text-[10px] font-medium text-violet-600 uppercase tracking-wide">Vertical</span>
                    <span className="text-xs text-muted-foreground truncate max-w-[140px]">
                      {selectedVertical.filename}
                    </span>
                    <button
                      onClick={handleEnterVerticalMode}
                      className="text-[10px] text-primary hover:underline"
                    >
                      Change
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleEnterVerticalMode}
                      className="h-32 w-40 rounded-lg border border-dashed border-muted-foreground/30 hover:border-primary/50 hover:bg-accent/50 transition-colors flex flex-col items-center justify-center gap-1"
                    >
                      <ImagePlus className="h-5 w-5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Add vertical asset</span>
                    </button>
                    <span className="text-[10px] font-medium text-violet-600 uppercase tracking-wide">Vertical</span>
                    <span className="text-[10px] text-muted-foreground">(optional)</span>
                  </>
                )}
              </div>

              {/* Horizontal slot - only for Single Image/Video */}
              {isSingleFormat && (
                <>
                  <div className="flex items-center h-32 text-muted-foreground text-lg font-light">+</div>

                  <div className="flex flex-col items-center gap-1.5">
                    {selectedHorizontal ? (
                      <>
                        <div className="relative h-32 w-40 rounded-lg border border-amber-500 bg-muted/30 flex items-center justify-center">
                          <img
                            src={selectedHorizontal.thumbnailUrl || ""}
                            alt="Horizontal"
                            className="h-[63px] w-28 rounded-md object-cover"
                          />
                          <button
                            onClick={handleClearHorizontal}
                            className="absolute top-1.5 right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 hover:bg-destructive/80"
                          >
                            <X className="h-2.5 w-2.5" />
                          </button>
                        </div>
                        <span className="text-[10px] font-medium text-amber-600 uppercase tracking-wide">Horizontal</span>
                        <span className="text-xs text-muted-foreground truncate max-w-[140px]">
                          {selectedHorizontal.filename}
                        </span>
                        <button
                          onClick={handleEnterHorizontalMode}
                          className="text-[10px] text-primary hover:underline"
                        >
                          Change
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={handleEnterHorizontalMode}
                          className="h-32 w-40 rounded-lg border border-dashed border-muted-foreground/30 hover:border-primary/50 hover:bg-accent/50 transition-colors flex flex-col items-center justify-center gap-1"
                        >
                          <ImagePlus className="h-5 w-5 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Add horizontal asset</span>
                        </button>
                        <span className="text-[10px] font-medium text-amber-600 uppercase tracking-wide">Horizontal</span>
                        <span className="text-[10px] text-muted-foreground">(optional)</span>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Footer - hidden in single select mode (click-to-select) */}
        {!singleSelectMode && (
          <div className="flex items-center justify-between pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              {filteredCreatives.length} creative{filteredCreatives.length !== 1 ? "s" : ""}
              {searchQuery || aspectTab !== "all" || typeFilter !== "all" || selectionMode !== "feed"
                ? " (filtered)"
                : ""}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={!selectedFeedId}>
                Select
              </Button>
            </div>
          </div>
        )}
        {singleSelectMode && (
          <div className="flex items-center justify-between pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              {filteredCreatives.length} creative{filteredCreatives.length !== 1 ? "s" : ""} — click to select
            </p>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
